<template>
  <div>
    <!-- Offer Filters -->
    <Card additional-classes="slds-card_boundary" :title="$t('campaignDesigner.targetDefinition.contentFilters')">
      <button class="slds-button slds-button_neutral" v-if="!loading" @click="showModal = true" slot="actions">
        {{$t(`campaignDesigner.targetDefinition.manageMvsVin`)}}
      </button>
      <!-- Brand, CarAge -->
      <div class="row">
        <div class="slds-form-element col slds-size--medium">
          <BrandFilter
            :brands="allBrands"
            labels-prefix="campaignDesigner.targetDefinition.brands"
            mode="view"
            ref="filter_brand"
            v-model="brands"
          />
        </div>

        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :filter-mode="filterMode"
            :label="$t('campaignDesigner.targetDefinition.carAge')"
            :original-value="originalValues"
            filter-type="carAge"
            ref="filter_carAge"
            v-if="isCreate || isEdit"
            v-model="carAges"
          />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.carAge')"
            :value="carAgeDisplayText | capitalize"
            type="text"
            v-else />
        </div>
      </div>

      <!-- Mileage, Fuel Type -->
      <div class="row slds-m-top--medium">
        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :filter-mode="filterMode"
            :label="$t('campaignDesigner.targetDefinition.mileages')"
            :original-value="originalValues"
            input-type="number"
            ref="filter_mileage"
            filter-type="range"
            v-if="isCreate || isEdit"
            v-model="mileages" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.mileages')"
            :value="mileages"
            type="range"
            v-else />
        </div>

        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :filter-mode="filterMode"
            :label="$t('campaignDesigner.targetDefinition.fuelTypes.label')"
            :options="config.fuelTypes.options"
            filter-type="included"
            labels-prefix="campaignDesigner.targetDefinition.fuelTypes"
            is-multi-picklist
            ref="filter_fuelType"
            v-if="isCreate || isEdit"
            v-model="fuelTypes" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.fuelTypes.label')"
            :options="config.fuelTypes.options"
            :value="fuelTypes"
            labels-prefix="campaignDesigner.targetDefinition.fuelTypes"
            type="multipicklist"
            v-else />
        </div>
      </div>

      <!-- Model Year From, Segment -->
      <div class="row slds-m-top--medium">
        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :filter-mode="filterMode"
            :label="$t('campaignDesigner.targetDefinition.modelYearFrom')"
            :options="config.modelYearFrom.options"
            filter-type="included"
            is-multi-picklist
            ref="filter_modelYearFrom"
            v-if="isCreate || isEdit"
            v-model="modelYearFrom" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.modelYearFrom')"
            :options="config.modelYearFrom.options"
            :value="modelYearFrom"
            type="array"
            v-else />
        </div>

        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :filter-mode="filterMode"
            :label="$t('campaignDesigner.targetDefinition.segment')"
            :options="config.segment.options"
            filter-type="included"
            is-multi-picklist
            ref="filter_segment"
            v-if="isCreate || isEdit"
            v-model="segment"
          />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.segment')"
            :options="config.segment.options"
            :value="segment"
            type="array"
            v-else />
        </div>
      </div>

      <!-- Engine Size, Dealer Cluster -->
      <div class="row slds-m-top--medium">
        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :filter-mode="filterMode"
            :label="$t('campaignDesigner.targetDefinition.engineSize')"
            :options="config.engineSize.options"
            filter-type="included"
            is-multi-picklist
            ref="filter_engineSize"
            v-if="isCreate || isEdit"
            v-model="engineSize" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.engineSize')"
            :options="config.engineSize.options"
            :value="engineSize"
            type="array"
            v-else />
        </div>

        <div class="slds-form-element col slds-size--medium" v-if="config.dealerCluster">
            <DealerClusterFilter
              :editable="isCreate || isEdit"
              :filter-mode="filterMode"
              :options="config.dealerCluster.options"
              :selected-dealers-map="dealers"
              @dealersUpdate="handleDealersUpdate"
              v-model="dealerCluster"/>
        </div>
        <div class="slds-form-element col slds-size--medium" v-else>
          <span>DEALER CLUSTERS NOT AVAILABLE</span>
        </div>
      </div>

      <!-- Fleet -->
      <div class="row slds-m-top--medium">
        <div class="slds-form-element col slds-size--medium">
          <Picklist
            :label="$t('campaignDesigner.targetDefinition.fleet.label')"
            :options="config.fleet.options"
            :tooltip="$t('campaignDesigner.targetDefinition.fleet.tooltip')"
            labels-prefix="filters"
            searchable
            v-if="filterMode === 'add-remove' && (isCreate || isEdit)"
            v-model="fleet" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.fleet.label')"
            :options="config.fleet.options"
            :tooltip="$t('campaignDesigner.targetDefinition.fleet.tooltip')"
            :value="fleet"
            labels-prefix="filters"
            type="picklist"
            v-else />
        </div>
      </div>

      <!-- Target Cluster -->
      <div class="row slds-m-top--medium">
        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :filter-mode="filterMode"
            :label="$t('campaignDesigner.targetDefinition.targetCluster.label')"
            :options="config.targetCluster.options"
            filter-type="included"
            is-multi-picklist
            labels-prefix="campaignDesigner.targetDefinition.targetCluster"
            ref="filter_targetCluster"
            v-if="isCreate || isEdit"
            v-model="targetCluster"
          />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.targetCluster.label')"
            :options="config.targetCluster.options"
            :value="targetCluster"
            labels-prefix="campaignDesigner.targetDefinition.targetCluster"
            type="multipicklist"
            v-else
          />
        </div>
      </div>
    </Card>

    <!-- Customer Filters -->
    <Card additional-classes="slds-card_boundary" :title="$t('campaignDesigner.targetDefinition.customerFilters')">
      <template slot="actions">
        <button class="slds-button slds-button_neutral" v-if="!userPreferences.onlyInformativeCampaign && (isCreate || isEdit)" @click="applyToOtherSections">
          {{$t('campaignDesigner.targetDefinition.applyToOtherSections')}}
        </button>
        <button class="slds-button slds-button_neutral" @click="showManageZipCodesModal = true">
          {{$t(`campaignDesigner.targetDefinition.manageZipCodes.label`)}}
        </button>
      </template>
      <!-- Channel, Communication Channels filter logic -->
      <div class="row slds-m-top--medium">
        <div class="slds-form-element col slds-size--medium">
          <ChannelFilter
            :channels="channelOptions"
            labels-prefix="channels"
            mode="view"
            v-model="channels"
          />
        </div>

        <div class="slds-form-element col slds-size--medium">
          <Picklist
            :label="$t('campaignDesigner.targetDefinition.communicationChannelsFilterLogic.label')"
            :options="config.communicationChannelsFilterLogic.options"
            :validation="['required']"
            ref="filter_channelsLogic"
            labels-prefix="campaignDesigner.targetDefinition.communicationChannelsFilterLogic"
            v-if="isCreate || isEdit"
            v-model="communicationChannelsFilterLogic" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.communicationChannelsFilterLogic.label')"
            :options="config.communicationChannelsFilterLogic.options"
            :value="communicationChannelsFilterLogic"
            labels-prefix="campaignDesigner.targetDefinition.communicationChannelsFilterLogic"
            type="picklist"
            v-else />
        </div>
      </div>

      <!-- Registration on MOC, Exclude Contacts Without Associated Dealer -->
      <div class="row slds-m-top--medium">
        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :label="$t('campaignDesigner.targetDefinition.registrationOnMOC')"
            filter-type="exact"
            ref="filter_registrationOnMOC"
            v-if="isCreate || isEdit"
            v-model="registrationOnMOC" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.registrationOnMOC')"
            :value="registrationOnMOC"
            type="boolean"
            v-else />
        </div>

        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :label="$t('campaignDesigner.targetDefinition.excludeContactsWithoutAssociatedDealer')"
            :tooltip="$t('campaignDesigner.targetDefinition.dealerCluster.tooltip')"
            filter-type="exact"
            ref="filter_excludeContactsWithoutAssociatedDealer"
            v-if="isCreate || isEdit"
            v-model="excludeContactsWithoutAssociatedDealer" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.excludeContactsWithoutAssociatedDealer')"
            :tooltip="$t('campaignDesigner.targetDefinition.dealerCluster.tooltip')"
            :value="excludeContactsWithoutAssociatedDealer"
            type="boolean"
            v-else />
        </div>
      </div>

      <!-- Client Type -->
      <div class="row slds-m-top--medium">

        <div class="slds-form-element col slds-size--medium">
          <GenericFilter
            :label="$t('campaignDesigner.targetDefinition.clientType.label')"
            :options="config.clientType.options"
            filter-type="included"
            labels-prefix="campaignDesigner.targetDefinition.clientType"
            is-picklist
            ref="filter_clientType"
            v-if="isCreate || isEdit"
            v-model="clientType"
          />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.clientType.label')"
            :options="config.clientType.options"
            :value="clientType"
            labels-prefix="campaignDesigner.targetDefinition.clientType"
            type="picklist"
            v-else
          />
        </div>
      </div>

      <!-- Marketing Privacy Consent, Profiling Privacy Consent -->
      <div class="row slds-m-top--medium">
        <div class="slds-form-element col slds-size--medium">
          <Picklist
            :label="$t('campaignDesigner.targetDefinition.marketingPrivacyConsent')"
            :options="config.marketingPrivacyConsent.options"
            :validation="['required']"
            hide-empty-value
            labels-prefix="consent"
            ref="filter_marketingPrivacyConsent"
            v-if="marketingConsentEditable && (isCreate || isEdit)"
            v-model="marketingPrivacyConsent" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.marketingPrivacyConsent')"
            :options="config.marketingPrivacyConsent.options"
            :value="marketingPrivacyConsent"
            labels-prefix="consent"
            type="picklist"
            v-else />
        </div>

        <div class="slds-form-element col slds-size--medium">
          <Picklist
            :label="$t('campaignDesigner.targetDefinition.profilingPrivacyConsent')"
            :options="config.profilingPrivacyConsent.options"
            :validation="['required']"
            hide-empty-value
            labels-prefix="consent"
            ref="filter_privacy"
            v-if="isCreate || isEdit"
            v-model="profilingPrivacyConsent" />

          <OutputField
            :label="$t('campaignDesigner.targetDefinition.profilingPrivacyConsent')"
            :options="config.profilingPrivacyConsent.options"
            :value="profilingPrivacyConsent"
            labels-prefix="consent"
            type="picklist"
            v-else />
        </div>
      </div>

      <!-- OCF Date, First Registration Date -->
      <div class="row slds-m-top--medium">
        <div class="slds-form-element col">
          <div class="slds-size--medium">

          <GenericFilter
            :label="$t('campaignDesigner.targetDefinition.ocfDate')"
            :original-value="originalValues"
            filter-mode="add-remove"
            input-type="date"
            ref="filter_ocfDate"
            filter-type="range"
            v-if="isCreate || isEdit"
            v-model="ocfDate" />

            <OutputField
              :label="$t('campaignDesigner.targetDefinition.ocfDate')"
              :value="ocfDate"
              item-type="date"
              type="range"
              v-else/>
          </div>
        </div>

        <div class="slds-form-element col">
          <div class="slds-size--medium">
            <GenericFilter
              filter-mode="add-remove"
              :label="$t('campaignDesigner.targetDefinition.firstRegistrationDate')"
              filter-type="range"
              input-type="date"
              ref="filter_firstRegistrationDate"
              v-if="isCreate || isEdit"
              v-model="firstRegistrationDate" />

            <OutputField
              :label="$t('campaignDesigner.targetDefinition.firstRegistrationDate')"
              :value="firstRegistrationDate"
              item-type="date"
              type="range"
              v-else/>
          </div>
        </div>
      </div>

      <Accordion class="slds-m-top--medium">
        <AccordionItem :title="$t('campaignDesigner.targetDefinition.warrantyExtension')" accordion-item-id="warrantyExtension">
          <!-- Warranty Extension-->
          <div class="row slds-m-top--medium">
            <div class="slds-form-element col row slds-size--medium">
              <div v-if="isCreate || isEdit">
                <Picklist
                  :label="$t('campaignDesigner.targetDefinition.warrantyExtension')"
                  :options="config.warrantyExtensionInclude.options"
                  labels-prefix="filters"
                  v-model="warrantyExtensionInclude" />

                <Input
                  :disabled="warrantyExtensionInclude === 'noFilter'"
                  :label="$t('general.from')"
                  :validation="[{name: 'is-date-before', param: warrantyExtension.toDate}]"
                  ref="filter_warrantyExtension_fromDate"
                  type="date"
                  v-model="warrantyExtension.fromDate" />

                <Input
                  :disabled="warrantyExtensionInclude === 'noFilter'"
                  :label="$t('general.to')"
                  ref="filter_warrantyExtension_toDate"
                  type="date"
                  v-model="warrantyExtension.toDate" />
              </div>

              <div v-else>
                <OutputField
                  :label="$t('campaignDesigner.targetDefinition.warrantyExtension')"
                  :options="config.warrantyExtensionInclude.options"
                  :value="warrantyExtensionInclude"
                  labels-prefix="filters"
                  type="picklist" />

                <OutputField type="custom" v-if="warrantyExtension.fromDate && warrantyExtension.toDate">
                  {{$t('general.from')}} {{warrantyExtension.fromDate | formatDate($t('general.dateFormat'))}} {{$t('general.to')}} {{warrantyExtension.toDate | formatDate($t('general.dateFormat'))}}
                </OutputField>
              </div>
            </div>

            <div class="slds-form-element col row slds-size--medium">
              <div v-if="isCreate || isEdit">
                <Input
                  :label="$t('campaignDesigner.targetDefinition.carsWithoutWarranties')"
                  :disabled="warrantyExtensionInclude === 'noFilter'"
                  type="checkbox"
                  v-model="warrantyExtension.carsWithoutWarranties.selected" />

                <Picklist
                  :options="config.warrantyExtension.contracts.options"
                  :disabled="warrantyExtensionInclude === 'noFilter'"
                  :label="$t('campaignDesigner.targetDefinition.contracts')"
                  multipicklist
                  searchable
                  ref="filter_warrantyExtension_contracts"
                  v-model="warrantyExtension.contracts.selected" />
              </div>

              <div v-else>
                <OutputField
                  :label="$t('campaignDesigner.targetDefinition.carsWithoutWarranties')"
                  :value="warrantyExtension.carsWithoutWarranties.selected"
                  type="checkbox" />

                <OutputField
                  :label="$t('campaignDesigner.targetDefinition.contracts')"
                  :options="config.warrantyExtension.contracts.options"
                  :value="warrantyExtension.contracts.selected"
                  type="multipicklist"
                  v-if="warrantyExtension.contracts && warrantyExtension.contracts.selected.length" />
              </div>
            </div>
          </div>
        </AccordionItem>
        <AccordionItem
          accordion-item-id="indexes"
          :title="`${$t('campaignDesigner.targetDefinition.engagement.label')}, ${$t('campaignDesigner.targetDefinition.pressure.label')}, ${$t('campaignDesigner.targetDefinition.reactivity.label')}`">
          <Accordion>
            <AccordionItem :title="$t('channels.email')" accordion-item-id="emailIndexes">
              <div class="row slds-m-top--medium">
                <div class="slds-form-element col row slds-size--medium">
                  <Picklist
                    :disabled="isContactPlan"
                    :label="$t('campaignDesigner.targetDefinition.engagement.label')"
                    :options="config.emailEngagement.options"
                    :tooltip="$t('campaignDesigner.targetDefinition.engagement.tooltip')"
                    labels-prefix="campaignDesigner.targetDefinition.engagement"
                    show-empty-value
                    tooltip-direction="right"
                    v-if="isCreate || isEdit"
                    v-model="emailEngagement" />

                  <OutputField
                    :label="$t('campaignDesigner.targetDefinition.engagement.label')"
                    :options="config.emailEngagement.options"
                    :tooltip="$t('campaignDesigner.targetDefinition.engagement.tooltip')"
                    :value="emailEngagement"
                    labels-prefix="campaignDesigner.targetDefinition.engagement"
                    tooltip-direction="right"
                    type="picklist"
                    v-else />
                </div>

                <div class="slds-form-element col row slds-size--medium">
                  <Picklist
                    :label="$t('campaignDesigner.targetDefinition.reactivity.label')"
                    :options="config.emailReactivity.options"
                    :tooltip="$t('campaignDesigner.targetDefinition.reactivity.tooltip')"
                    labels-prefix="campaignDesigner.targetDefinition.reactivity"
                    show-empty-value
                    tooltip-direction="right"
                    v-if="isCreate || isEdit"
                    v-model="emailReactivity" />

                  <OutputField
                    :label="$t('campaignDesigner.targetDefinition.reactivity.label')"
                    :options="config.emailReactivity.options"
                    :tooltip="$t('campaignDesigner.targetDefinition.reactivity.tooltip')"
                    :value="emailReactivity"
                    labels-prefix="campaignDesigner.targetDefinition.reactivity"
                    tooltip-direction="right"
                    type="picklist"
                    v-else />
                </div>
              </div>

              <div class="row slds-m-top--medium">
                <div class="slds-form-element col row slds-size--medium">
                  <Picklist
                    :label="$t('campaignDesigner.targetDefinition.pressure.label')"
                    :options="config.emailPressure.options"
                    labels-prefix="campaignDesigner.targetDefinition.pressure"
                    show-empty-value
                    v-if="isCreate || isEdit"
                    v-model="emailPressure" />

                  <OutputField
                    :label="$t('campaignDesigner.targetDefinition.pressure.label')"
                    :options="config.emailPressure.options"
                    :value="emailPressure"
                    labels-prefix="campaignDesigner.targetDefinition.pressure"
                    type="picklist"
                    v-else />
                </div>
              </div>
            </AccordionItem>
          </Accordion>
        </AccordionItem>
      </Accordion>
    </Card>

    <!-- CP Type Target Selection -->
    <CPTypeTargetSelection
      :editable="isCreate || isEdit"
      :engagement-conditions="engagementConditions"
      :engagement-options="config.emailEngagement.options"
      :is-informative-campaign="userPreferences.onlyInformativeCampaign"
      :maintenance-model-cluster="maintenanceModelCluster"
      :maintenance-model-cluster-options="config.maintenanceModelCluster.options"
      :mileage-tag="mileageTag"
      :mileage-tag-options="config.mileageTag.options"
      :months-from-first-registration-date="monthsFromFirstRegistrationDate"
      :show-filters="isContactPlan"
      :validation="['custom']"
      ref="filter_cp"
      v-if="isContactPlan"
      @apply-to-other-sections-cp="applyToOtherSectionsCP"
      @update="updateCPTypeTarget" />

    <!-- Manage MVS / VIN -->
    <transition name="fade">
      <ManageVINMVS
        :brands="selectedBrands"
        :can-exclude-vin="!sectionNumber || userPreferences.userCanEditExcludedVIN[sectionNumber]"
        :editable="isCreate || isEdit"
        :informative-campaign="userPreferences.onlyInformativeCampaign"
        :mvs="mvs"
        :mvs-to-exclude="mvsToExclude"
        :vin="vin"
        :vin-to-exclude="vinToExclude"
        v-if="showModal"
        @close="showModal = false"
        @save="updateMvsVin" />
    </transition>

    <transition name="fade">
      <ManageZIPCodes
        v-if="showManageZipCodesModal"
        v-model="zipCodes"
        @close="showManageZipCodesModal = false"
        :editable="isCreate || isEdit" />
    </transition>
  </div>
</template>
<script>

import moment from 'moment'
import { validate } from '../../../utils/utils'
import Accordion from '../../../slds/Accordion'
import AccordionItem from '../../../slds/AccordionItem'
import BrandFilter from './BrandFilter'
import Card from '../../../slds/Card'
import CPTypeTargetSelection from './CPTypeTargetSelection'
import ChannelFilter from './ChannelFilter'
import DealerClusterFilter from './DealerClusterFilter'
import GenericFilter from './../../filters/GenericFilter.vue'
import ManageZIPCodes from './ManageZIPCodes.vue'
import ManageVINMVS from './ManageVINMVS.vue'
import Input from '../../../slds/Input'
import OutputField from '../../../slds/OutputField'
import Picklist from '../../../slds/Picklist'

const CONTACT_PLAN_CT = [
  'as_contact_plan',
  'as_service_contact_plan'
]

export default {
  props: {
    allBrands: Array,
    brands: Array,
    config: Object,
    isCreate: Boolean,
    isEdit: Boolean,
    loading: Boolean,
    originalValues: Object,
    sectionNumber: String,
    userPreferences: Object,
    value: Object
  },
  components: {
    Accordion,
    AccordionItem,
    BrandFilter,
    Card,
    ChannelFilter,
    CPTypeTargetSelection,
    DealerClusterFilter,
    GenericFilter,
    Input,
    ManageZIPCodes,
    ManageVINMVS,
    OutputField,
    Picklist
  },
  data () {
    return {
      carAges: {},
      channels: [],
      channelOptions: [],
      clientType: '',
      communicationChannelsFilterLogic: false,
      dealers: {},
      dealerCluster: [],
      emailEngagement: '',
      emailPressure: '',
      emailReactivity: '',
      engagementConditions: [],
      excludeContactsWithoutAssociatedDealer: false,
      engineSize: [],
      filterTypeToInclude: '',
      filterTypeToIncludeOptions: [
        {
          label: this.$t('campaignDesigner.targetDefinition.mvs'),
          value: 'mvs'
        },
        {
          label: this.$t('campaignDesigner.targetDefinition.vin'),
          value: 'vin'
        }
      ],
      firstRegistrationDate: '',
      fleet: '',
      fuelTypes: [],
      isContactPlan: false,
      marketingConsentEditable: false,
      marketingPrivacyConsent: '',
      mileages: [],
      modalTabOpen: '',
      modelYearFrom: [],
      maintenanceModelCluster: [],
      mileageTag: '',
      monthsFromFirstRegistrationDate: 0,
      mvs: {},
      mvsToExclude: {},
      ocfDate: [],
      openMVSModalSections: [],
      openVINModalSections: [],
      profilingPrivacyConsent: '',
      purchasedServicesAndAccessories: '',
      registrationOnMOC: '',
      segment: [],
      showModal: false,
      showManageZipCodesModal: false,
      targetCluster: [],
      targetRange: [],
      vin: [],
      vinToExclude: [],
      warrantyExtension: '',
      warrantyExtensionInclude: '',
      zipCodes: []
    }
  },
  async created () {
    // Map selected values
    this.mapValue(this.value)
  },
  mounted () {
    // Set Marketing consent editable if campaign is only informative
    if (this.userPreferences.onlyInformativeCampaign) {
      this.marketingConsentEditable = true
    } else {
      this.marketingPrivacyConsent = 'Y'
      this.$emit('filterChanged', { fieldName: 'marketingPrivacyConsent', newValue: 'Y' })
      this.marketingConsentEditable = false
    }
  },
  computed: {
    carAgeDisplayText () {
      if (this.carAges && this.carAges.timeRangeSelector) {
        const { range } = this.carAges
        switch (this.carAges.timeRangeSelector) {
          case 'touchpoint':
            return `${this.$t('general.from')} ${range.touchpoint.touchpointFrom} ${this.$t('general.to')} ${range.touchpoint.touchpointTo}`
          case 'date':
            const formattedDateFrom = moment(range.date.dateFrom).format(this.$t('general.dateFormat'))
            const formattedDateTo = moment(range.date.dateTo).format(this.$t('general.dateFormat'))
            return `${this.$t('general.from')} ${formattedDateFrom} ${this.$t('general.to')} ${formattedDateTo}`
          case 'age':
            return `${range.age.monthAge} ${this.$t('general.months')}`
          default:
            throw new Error(`Unhandled type ${this.carAges.timeRangeSelector}`)
        }
      } else {
        return '\xa0'
      }
    },
    filterTypeToIncludeEditable () {
      return this.userPreferences.onlyInformativeCampaign &&
        (!this.tempMvs || !Object.keys(this.tempMvs)) &&
        (!this.tempVin || !this.tempVin.length)
    },
    filterMode () {
      if (this.userPreferences.onlyInformativeCampaign) {
        return 'add-remove'
      } else {
        return 'remove-only'
      }
    },
    carFilterType () {
      if (this.userPreferences.onlyInformativeCampaign) {
        return 'NO_OFFER'
      } else if (this.vin) {
        return 'VIN'
      } else {
        return 'MVS'
      }
    },
    selectedBrands () {
      return this.allBrands.filter(brand => this.brands.includes(brand.value))
    }
  },
  methods: {
    applyToOtherSections () {
      this.$emit('applyToOtherSections', { value: this.mapLocalValue(), sectionNumber: this.sectionNumber })
    },
    applyToOtherSectionsCP () {
      const valuesToApply = {
        engagementConditions: this.engagementConditions,
        maintenanceModelCluster: this.maintenanceModelCluster,
        mileageTag: this.mileageTag,
        monthsFromFirstRegistrationDate: this.monthsFromFirstRegistrationDate
      }
      this.$emit('applyToOtherSections', { value: valuesToApply, sectionNumber: this.sectionNumber })
    },
    closeModal () {
      this.showModal = false
      this.tempMvs = {}
      this.tempVin = []
      this.tempVinToExclude = []
    },
    handleDealersUpdate (event) {
      const { value } = event
      this.$emit('filterChanged', { fieldName: 'dealers', newValue: value })
    },

    mapLocalValue () {
      const mappedBrands = {
        options: this.allBrands,
        selected: this.brands
      }

      return {
        brands: mappedBrands,
        carAges: this.carAges,
        communicationChannelsFilterLogic: this.communicationChannelsFilterLogic,
        clientType: this.clientType,
        dealerCluster: this.dealerCluster,
        emailEngagement: this.emailEngagement,
        emailPressure: this.emailPressure,
        emailReactivity: this.emailReactivity,
        excludeContactsWithoutAssociatedDealer: this.excludeContactsWithoutAssociatedDealer,
        engineSize: this.engineSize,
        fleet: this.fleet,
        fuelTypes: this.fuelTypes,
        marketingPrivacyConsent: this.marketingPrivacyConsent,
        mileages: this.mileages,
        modelYearFrom: this.modelYearFrom,
        mvs: this.mvs,
        profilingPrivacyConsent: this.profilingPrivacyConsent,
        purchasedServicesAndAccessories: this.purchasedServicesAndAccessories,
        registrationOnMOC: this.registrationOnMOC,
        segment: this.segment,
        targetCluster: this.targetCluster,
        targetRange: this.targetRange,
        vin: this.vin,
        vinToExclude: this.vinToExclude,
        warrantyExtension: this.warrantyExtension,
        warrantyExtensionInclude: this.warrantyExtensionInclude,
        zipCodes: this.zipCodes
      }
    },
    mapValue (value) {
      Object.keys(value).forEach(data => {
        // No offers. Map 1:1 with data fields
        if (this.hasOwnProperty(data)) {
          const value = this.value[data]
          if (value !== undefined) {
            if (typeof value === 'object' && value && value.selected !== undefined) {
              this.$data[data] = value.selected
            } else {
              this.$data[data] = value
            }
          }

          this.$watch(data, (newValue, oldValue) => {
            this.$emit('filterChanged', { fieldName: data, newValue })
          })
        }
      })

      if (this.mvs && Object.keys(this.mvs).length) {
        this.filterTypeToInclude = 'mvs'
      } else if (this.vin && this.vin.length) {
        this.filterTypeToInclude = 'vin'
      }
      this.carAges = value.carAges
      this.warrantyExtension = value.warrantyExtension

      const campaign = this.$store.getters['campaignDesigner/state'].currentCampaign
      const campaignConfig = this.$store.getters['campaignDesigner/state'].currentCampaignConfig
      this.isContactPlan = CONTACT_PLAN_CT.includes(campaign.type)
      this.channels = campaign.config.campaignData.campaignChannel.selected || campaign.config.campaignData.campaignChannel
      this.channelOptions = campaignConfig.campaignData.campaignChannel.options
    },
    updateCPTypeTarget (event) {
      const { field, newValue } = event

      this.$data[field] = newValue
    },
    updateMvsVin (event) {
      const { mvs, mvsToExclude, vin, vinToExclude } = event.data

      this.$emit('filterChanged', { fieldName: 'mvs', newValue: mvs })
      this.$emit('filterChanged', { fieldName: 'mvsToExclude', newValue: mvsToExclude })
      this.$emit('filterChanged', { fieldName: 'vin', newValue: vin })
      this.$emit('filterChanged', { fieldName: 'vinToExclude', newValue: vinToExclude })

      this.showModal = false
    },
    validate () {
      if (this.isCreate || this.isEdit) {
        return validate(this, 'filter')
      } else {
        return true
      }
    }
  },
  watch: {
    warrantyExtensionInclude (newValue) {
      if (newValue === 'noFilter') {
        this.warrantyExtension.fromDate = null
        this.warrantyExtension.toDate = null
        this.warrantyExtension.contracts.selected = []
        this.warrantyExtension.carsWithoutWarranties.selected = false
      }
      this.$emit('filterChanged', { fieldName: 'warrantyExtensionInclude', newValue })
    },
    // showCPFilters (newValue, oldValue) {
    //   if (oldValue === true && newValue === false) {
    //     this.$emit('filterChanged', { fieldName: 'engagementConditions', newValue: [] })
    //   }
    // },
    warrantyExtension: {
      handler (newValue) {
        this.$emit('filterChanged', { fieldName: 'warrantyExtension', newValue })
      },
      deep: true
    },
    value: {
      handler (newValue) {
        this.mapValue(newValue)
      },
      deep: true
    }
  }
}
</script>
<style lang="scss" scoped>
Input {
  width: unset !important;
}

svg {
  cursor: pointer;
}

.brands-alert {
  height: 100%;
  width: 100%;
  top: 0;

  & > div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
.full-width {
  width: 100%;
}

.row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
}

.col {
  flex-grow: 1;
}
</style>
